import { AbsoluteTimeRange } from '@chronosphereio/core';
import { MarkAreaOption, MarkLineOption } from 'echarts/types/dist/shared';
import { useMemo } from 'react';
import { findTooltipEndTime, ShowEndTimeArgs } from '@/components';
import {
  EVENT_SERIES_NAME,
  EventPayload,
  EventSeriesOption,
  TimeScale,
  TimeSeriesDataset,
  TimeSeriesOption,
  TimeSeriesWithoutValues,
} from '@/core';
import { ClickSeriesData } from '../time-series-chart-model';

const SYMBOL_OFFSET = [0, -10]; // make sure event icon sits right above the grid

/**
 * Hook to create event series from EventEvents
 */
export function useComputeEventSeries({ events = [], startTime }: { events: EventPayload[]; startTime: number }) {
  return useMemo(() => {
    if (!events || events.length === 0) {
      return null;
    }

    // TODO: Don't hardcode this. Either make it configurable or compute it from the data.
    const defaultColor = '#8690B6';

    // Events with no end time are lines, events with end time are areas
    const eventLines: MarkLineOption['data'] = [];
    const eventAreas: MarkAreaOption['data'] = [];

    // use scatterplot to show icon for each event
    const eventDots: EventSeriesOption[] = [];

    for (const event of events) {
      const { timestampMs, endTimestampMs, icon, color } = event;
      const eventColor = color ?? defaultColor;

      if (endTimestampMs && endTimestampMs !== timestampMs) {
        eventAreas.push([
          {
            xAxis: timestampMs,
            itemStyle: {
              borderColor: eventColor,
              borderWidth: 1,
              color: eventColor,
              opacity: 0.1,
            },
          },
          { xAxis: endTimestampMs },
        ]);
        eventDots.push({
          symbol: icon,
          symbolOffset: SYMBOL_OFFSET,
          itemStyle: {
            color,
            opacity: 1,
          },
          value: [endTimestampMs, 1],
          event,
        });
      } else {
        eventLines.push({
          symbol: 'none',
          symbolSize: 0,
          itemStyle: {
            color: eventColor,
          },
          lineStyle: {
            type: 'solid',
          },
          xAxis: timestampMs,
        });
      }

      eventDots.push({
        symbol: icon,
        symbolOffset: SYMBOL_OFFSET,
        itemStyle: {
          color: eventColor,
          opacity: 1,
        },
        value: [timestampMs, 1],
        event,
      });
    }

    const lines = {
      name: 'Events',
      type: 'line',
      xAxisIndex: 0,
      data: [[startTime, null]],
      markArea: {
        data: eventAreas,
      },
      markLine: {
        label: {
          show: false,
        },
        symbol: 'none',
        lineStyle: {
          width: 2,
        },
        silent: true, // set it to true so markline label does not show on hover, but this disables all mouse events for the markline
        data: eventLines,
      },
    } as TimeSeriesOption;

    const scatter = {
      name: EVENT_SERIES_NAME,
      type: 'scatter',
      cursor: 'crosshair', // Hack to cover issue with cursor flipping from crosshair to pointer and back - potentially due to zoom being enabled by default
      yAxisIndex: 2,
      symbolSize: 20,
      color: defaultColor,
      data: eventDots,
    } as TimeSeriesOption;

    return [lines, scatter];
  }, [events, startTime]);
}

/**
 * Get the selected series given the click event
 */
interface GetClickSeriesData {
  seriesIndex?: number;
  dataIndex?: number;
  data?: unknown;
  series?: TimeSeriesWithoutValues;
  showTooltipEndTime?: ShowEndTimeArgs;
  timeChartData?: TimeSeriesDataset[];
  timeRange?: AbsoluteTimeRange;
  timeScale?: TimeScale;
}
export function getClickSeriesData({
  series,
  seriesIndex,
  dataIndex,
  data,
  showTooltipEndTime,
  timeChartData,
  timeRange,
  timeScale,
}: GetClickSeriesData) {
  if (!series) return; // return if no series is clicked

  let startMs;
  let endMs;
  let value;

  // Check if data is a time series value tuple
  // it could be undefined if no data point is clicked
  const isTimeSeriesValueTuple = Array.isArray(data) && data.length === 2;
  if (data && isTimeSeriesValueTuple) {
    startMs = data[0];
    // only return end time if showTooltipEndTime is defined
    endMs =
      showTooltipEndTime && timeChartData && timeRange && dataIndex !== undefined && seriesIndex !== undefined
        ? findTooltipEndTime({
            index: dataIndex,
            values: timeChartData[seriesIndex]?.source ?? [],
            timeRange,
            timeScale,
            showEndTime: showTooltipEndTime,
          })
        : undefined;
    value = data[1];
  }

  const seriesData: ClickSeriesData = {
    series,
    startMs,
    endMs,
    value,
  };

  return seriesData;
}
